import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  useCreateUnifiedOrder,
  type UnifiedOrderRequest,
  type ShipmentInput,
  type PackageInput,
} from "@/api/unified-orders";
import { useQueryClient } from "@tanstack/react-query";
import {
  Package,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
} from "lucide-react";
import { OrderConfigStep } from "./steps/order-config-step";
import {
  ShipmentDetailsStep,
  type ShipmentDetailsStepHandle,
} from "./steps/shipment-details-step";
import { PricingSelectionStep } from "./steps/pricing-selection-step";
import { PaymentSelectionStep } from "./steps/payment-selection-step";
import { motion, AnimatePresence } from "motion/react";

interface CreateUnifiedOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (orderId: string) => void;
}

const STEPS = [
  { id: "config", title: "Order Configuration", icon: Package },
  { id: "shipments", title: "Shipment Details", icon: Package },
  { id: "pricing", title: "Pricing & Selection", icon: Package },
  { id: "payment", title: "Payment Method", icon: Package },
];

export function CreateUnifiedOrderDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateUnifiedOrderDialogProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [orderData, setOrderData] = useState<Partial<UnifiedOrderRequest>>({
    shipments: [],
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showDetailedSummary, setShowDetailedSummary] = useState(false);
  const shipmentDetailsRef = useRef<ShipmentDetailsStepHandle>(null);

  const queryClient = useQueryClient();

  const createOrderMutation = useCreateUnifiedOrder({
    onSuccess: (data) => {
      setIsCreating(false);
      toast.success("Order Created Successfully", {
        description: `Order ${data.orderTrackingNumber} has been created with ${data.shipments.length} shipment(s).`,
      });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      onSuccess?.(data.orderId);
      handleOpenChange(false);
    },
    onError: (err) => {
      setIsCreating(false);
      // Phase 10b: 409 BOOKING_NOT_ATTACHABLE — the booking the admin chose
      // just got cancelled or its driver run started. Bounce back to pricing
      // step; the bookings query will refetch and exclude the now-stale row.
      const errCause = err as Error & {
        response?: {
          status?: number;
          data?: { error?: string; details?: { reason?: string } };
        };
      };
      const status = errCause?.response?.status;
      const body = errCause?.response?.data;
      // 409 QUOTE_NOT_USABLE — the quote expired (30 min), was already spent, or
      // was never for this shipment. There is no "confirm the new price" answer to
      // this: the server holds the price, so the only way forward is a FRESH quote.
      // Bounce to pricing, which re-fetches.
      if (status === 409 && body?.error === "QUOTE_NOT_USABLE") {
        toast.error("That quote is no longer valid", {
          description:
            "Rates move, so a quote is only good for a short while. Re-select a rate to continue.",
        });
        setCurrentStep(2); // back to pricing (admin: 0=config, 1=details, 2=pricing)
        return;
      }
      if (status === 409 && body?.error === "BOOKING_NOT_ATTACHABLE") {
        const reason = body.details?.reason;
        toast.error("Pickup no longer attachable", {
          description:
            reason === "cancelled"
              ? "That pickup was cancelled — please pick a new slot."
              : "That pickup just started — please pick a new slot.",
        });
        void queryClient.invalidateQueries({
          queryKey: ["attachablePickupBookings"],
        });
        setCurrentStep(2); // back to pricing step (admin: 0=config, 1=details, 2=pricing)
        return;
      }
      toast.error("Failed to Create Order", {
        description:
          err instanceof Error
            ? err.message
            : "There was an error creating the order.",
      });
    },
  });

  // Reset state when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Dialog is closing, reset state
      setCurrentStep(0);
      setOrderData({ shipments: [] });
      setIsCreating(false);
    }
    onOpenChange(open);
  };

  const handleNext = (stepData: Partial<UnifiedOrderRequest>) => {
    setOrderData((prev) => ({ ...prev, ...stepData }));
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep === 1) {
      const draft = shipmentDetailsRef.current?.snapshot();
      if (draft) setOrderData((prev) => ({ ...prev, ...draft }));
    }
    setCurrentStep((prev) => prev - 1);
  };

  const handleCreateOrder = (finalData: UnifiedOrderRequest) => {
    setIsCreating(true);

    // Pricing-redesign: the pricing step already attached `selectedRate` and
    // optional `pickup` to each shipment. Strip non-server fields (selectedPricing
    // is a UI-only shape used by the dialog summary) before submitting.
    //
    // Phase 10a.5: server's XOR refine rejects shipFrom + shipFromAddressBookEntryId
    // together. When the entry ID is set, drop the inline shipFrom (and same for
    // recipient).
    const cleanShipments = finalData.shipments.map(
      (s: ShipmentInput & { selectedPricing?: unknown }) => {
        const { selectedPricing: _ignore, ...rest } = s;
        if (rest.shipFromAddressBookEntryId) delete rest.shipFrom;
        if (rest.shipToAddressBookEntryId) delete rest.shipTo;
        return rest;
      }
    );
    const order: UnifiedOrderRequest = {
      ...finalData,
      shipments: cleanShipments,
    };

    createOrderMutation.mutate(order);
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const currentStepData = STEPS[currentStep];

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  };

  const getTotalPrice = () => {
    if (!orderData.shipments) return 0;
    return orderData.shipments.reduce(
      (
        sum: number,
        shipment: ShipmentInput & { selectedPricing?: { priceCents?: number } }
      ) => {
        return sum + (shipment.selectedPricing?.priceCents ?? 0);
      },
      0
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <OrderConfigStep data={orderData} onNext={handleNext} />;
      case 1:
        return (
          <ShipmentDetailsStep
            ref={shipmentDetailsRef}
            data={orderData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <PricingSelectionStep
            data={orderData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <PaymentSelectionStep
            data={orderData}
            onNext={(paymentData) => {
              const finalData = { ...orderData, ...paymentData };
              void handleCreateOrder(finalData as UnifiedOrderRequest);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] flex-col overflow-hidden p-4 sm:max-w-6xl sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Create Unified Order
          </DialogTitle>
          <DialogDescription>
            Create orders with multiple shipments and intelligent rate selection
          </DialogDescription>
        </DialogHeader>

        {/* Enhanced Step Progress Indicator */}
        <div className="space-y-3 pb-4 border-b">
          {/* Step Indicators with Labels */}
          <div className="relative flex items-start justify-between px-2">
            {STEPS.map((step, index) => {
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;

              return (
                <div
                  key={step.id}
                  className="flex flex-col items-center flex-1 relative"
                >
                  {/* Connector Line */}
                  {index < STEPS.length - 1 && (
                    <div
                      className="absolute top-5 left-1/2 w-full h-0.5 -z-10"
                      style={{
                        background: isCompleted
                          ? "hsl(142 76% 36%)"
                          : "hsl(var(--muted-foreground) / 0.2)",
                      }}
                    />
                  )}

                  {/* Step Circle */}
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                      isCompleted
                        ? "bg-green-500 border-green-500 text-white"
                        : isCurrent
                          ? "bg-primary border-primary text-primary-foreground"
                          : "bg-background border-muted-foreground/30 text-muted-foreground"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <span className="font-semibold text-sm">{index + 1}</span>
                    )}
                  </div>

                  {/* Step Label */}
                  <div className="mt-2 text-center max-w-[90px]">
                    <div
                      className={`text-xs font-medium transition-colors ${
                        isCurrent
                          ? "text-primary"
                          : isCompleted
                            ? "text-green-600"
                            : "text-muted-foreground"
                      }`}
                    >
                      {step.title}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div className="px-2">
            <Progress value={progress} className="h-1.5" />
          </div>

          {/* Current Step Info */}
          <div className="flex items-center justify-between px-2 pt-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {currentStepData.icon && (
                <currentStepData.icon className="h-4 w-4" />
              )}
              <span>{currentStepData.title}</span>
            </div>
            <Badge variant="secondary" className="text-xs">
              Step {currentStep + 1} of {STEPS.length}
            </Badge>
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Sticky Footer with Summary + Buttons */}
        {currentStep >= 0 && (
          <div className="border-t bg-muted/30">
            {/* Order Summary - Show from step 2 onwards */}
            {currentStep > 0 && (
              <div className="border-b border-border">
                {/* Compact Summary Bar */}
                <div
                  className="p-3 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setShowDetailedSummary(!showDetailedSummary)}
                >
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-muted-foreground">
                      <span className="font-medium">Shipments:</span>{" "}
                      <span className="text-foreground">
                        {(
                          orderData as UnifiedOrderRequest & {
                            _shipmentCount?: number;
                          }
                        )._shipmentCount ?? orderData.shipments?.length}
                      </span>
                    </div>
                    {orderData.shipments && orderData.shipments.length > 0 && (
                      <>
                        <div className="h-4 w-px bg-border" />
                        <div className="text-muted-foreground">
                          <span className="font-medium">Packages:</span>{" "}
                          <span className="text-foreground">
                            {orderData.shipments.reduce(
                              (sum: number, s: ShipmentInput) =>
                                sum + (s.packages?.length ?? 0),
                              0
                            )}
                          </span>
                        </div>
                      </>
                    )}
                    {Boolean(orderData.metadata?.referenceNumber) && (
                      <>
                        <div className="h-4 w-px bg-border" />
                        <div className="text-muted-foreground">
                          <span className="font-medium">Ref:</span>{" "}
                          <span className="text-foreground">
                            {String(orderData.metadata?.referenceNumber)}
                          </span>
                        </div>
                      </>
                    )}
                    {getTotalPrice() > 0 && (
                      <>
                        <div className="h-4 w-px bg-border" />
                        <div className="text-muted-foreground">
                          <span className="font-medium">Total:</span>{" "}
                          <span className="text-foreground font-semibold">
                            {formatCurrency(getTotalPrice())}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="text-xs">
                      {showDetailedSummary ? "Hide" : "View"} Details
                    </span>
                    {showDetailedSummary ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {/* Expandable Detailed Summary */}
                {showDetailedSummary &&
                  orderData.shipments &&
                  orderData.shipments.length > 0 && (
                    <div className="px-3 pb-3 bg-background/50 max-h-[400px] overflow-y-auto">
                      <div className="rounded-md border bg-card p-4 space-y-4">
                        {orderData.shipments.map(
                          (
                            shipment: ShipmentInput & {
                              selectedPricing?: {
                                priceCents?: number;
                                serviceName?: string;
                              };
                              selectedQuote?: { carrierName?: string };
                            },
                            index: number
                          ) => (
                            <div
                              key={index}
                              className="pb-4 border-b last:border-b-0"
                            >
                              {/* Shipment Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-primary" />
                                  <span className="font-semibold">
                                    Shipment {index + 1}
                                  </span>
                                </div>
                                {shipment.selectedPricing && (
                                  <div className="text-right">
                                    <div className="font-semibold text-primary">
                                      {formatCurrency(
                                        shipment.selectedPricing.priceCents ?? 0
                                      )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {
                                        (
                                          shipment.selectedPricing as {
                                            carrierName?: string;
                                            carrierDeliveryDays?: number;
                                          }
                                        )?.carrierName
                                      }{" "}
                                      - {shipment.selectedPricing.serviceName}
                                    </div>
                                    {(
                                      shipment.selectedPricing as {
                                        carrierName?: string;
                                        carrierDeliveryDays?: number;
                                      }
                                    )?.carrierDeliveryDays && (
                                      <div className="text-xs text-muted-foreground">
                                        {String(
                                          (
                                            shipment.selectedPricing as {
                                              carrierName?: string;
                                              carrierDeliveryDays?: number;
                                            }
                                          ).carrierDeliveryDays
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Sender & Recipient */}
                              <div className="grid grid-cols-2 gap-4 mb-3">
                                {/* Sender */}
                                <div className="space-y-1">
                                  <div className="text-xs font-medium text-muted-foreground">
                                    FROM
                                  </div>
                                  <div className="text-sm">
                                    <div className="font-medium">
                                      {shipment.senderName}
                                    </div>
                                    {shipment.senderEmail && (
                                      <div className="text-xs text-muted-foreground">
                                        {shipment.senderEmail}
                                      </div>
                                    )}
                                    {shipment.senderPhone && (
                                      <div className="text-xs text-muted-foreground">
                                        {shipment.senderPhone}
                                      </div>
                                    )}
                                    {shipment.shipFrom && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        <div>{shipment.shipFrom.street1}</div>
                                        {shipment.shipFrom.street2 && (
                                          <div>{shipment.shipFrom.street2}</div>
                                        )}
                                        <div>
                                          {shipment.shipFrom.city},{" "}
                                          {shipment.shipFrom.state}{" "}
                                          {shipment.shipFrom.postalCode}
                                        </div>
                                        <div>{shipment.shipFrom.country}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Recipient */}
                                <div className="space-y-1">
                                  <div className="text-xs font-medium text-muted-foreground">
                                    TO
                                  </div>
                                  <div className="text-sm">
                                    <div className="font-medium">
                                      {shipment.recipientName}
                                    </div>
                                    {shipment.recipientEmail && (
                                      <div className="text-xs text-muted-foreground">
                                        {shipment.recipientEmail}
                                      </div>
                                    )}
                                    {shipment.recipientPhone && (
                                      <div className="text-xs text-muted-foreground">
                                        {shipment.recipientPhone}
                                      </div>
                                    )}
                                    {shipment.shipTo && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        <div>{shipment.shipTo.street1}</div>
                                        {shipment.shipTo.street2 && (
                                          <div>{shipment.shipTo.street2}</div>
                                        )}
                                        <div>
                                          {shipment.shipTo.city},{" "}
                                          {shipment.shipTo.state}{" "}
                                          {shipment.shipTo.postalCode}
                                        </div>
                                        <div>{shipment.shipTo.country}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Packages */}
                              {shipment.packages &&
                                shipment.packages.length > 0 && (
                                  <div className="mt-3">
                                    <div className="text-xs font-medium text-muted-foreground mb-2">
                                      PACKAGES ({shipment.packages.length})
                                    </div>
                                    <div className="space-y-2">
                                      {shipment.packages.map(
                                        (
                                          pkg: PackageInput,
                                          pkgIndex: number
                                        ) => (
                                          <div
                                            key={pkgIndex}
                                            className="text-xs bg-muted/30 rounded p-2"
                                          >
                                            <div className="flex items-center justify-between">
                                              <span className="font-medium">
                                                Package {pkgIndex + 1}
                                              </span>
                                              <div className="flex items-center gap-2">
                                                {pkg.isFragile && (
                                                  <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                  >
                                                    Fragile
                                                  </Badge>
                                                )}
                                                {pkg.requiresCooling && (
                                                  <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                  >
                                                    Cooling
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>
                                            <div className="text-muted-foreground mt-1 space-y-0.5">
                                              <div>
                                                Weight: {pkg.weight}{" "}
                                                {pkg.weightUnit}
                                              </div>
                                              <div>
                                                Dimensions: {pkg.length} ×{" "}
                                                {pkg.width} × {pkg.height}{" "}
                                                {pkg.dimensionUnit}
                                              </div>
                                              {pkg.description && (
                                                <div>
                                                  Description: {pkg.description}
                                                </div>
                                              )}
                                              {(pkg.declaredValueCents ?? 0) >
                                                0 && (
                                                <div>
                                                  Value:{" "}
                                                  {formatCurrency(
                                                    pkg.declaredValueCents ?? 0
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>
                          )
                        )}

                        {/* Order Info */}
                        {(orderData.metadata?.referenceNumber ||
                          orderData.metadata?.notes ||
                          orderData.shipments?.[0]?.pickup?.scheduledDate) && (
                          <div className="pt-3 border-t">
                            <div className="text-xs font-medium text-muted-foreground mb-2">
                              ORDER INFORMATION
                            </div>
                            {Boolean(orderData.metadata?.referenceNumber) && (
                              <div className="text-sm mb-1">
                                <span className="text-muted-foreground">
                                  Reference:
                                </span>{" "}
                                {String(orderData.metadata?.referenceNumber)}
                              </div>
                            )}
                            {Boolean(orderData.metadata?.notes) && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">
                                  Notes:
                                </span>{" "}
                                {String(orderData.metadata?.notes)}
                              </div>
                            )}
                            {orderData.shipments?.[0]?.pickup
                              ?.scheduledDate && (
                              <div className="text-sm mt-1">
                                <span className="text-muted-foreground">
                                  Pickup Date:
                                </span>{" "}
                                {new Date(
                                  orderData.shipments[0].pickup.scheduledDate
                                ).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Total */}
                        {getTotalPrice() > 0 && (
                          <div className="flex items-center justify-between pt-3 border-t">
                            <span className="font-semibold text-lg">Total</span>
                            <span className="text-2xl font-bold text-primary">
                              {formatCurrency(getTotalPrice())}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col-reverse items-stretch gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
              {currentStep === 0 && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      // Trigger form submit in OrderConfigStep
                      const form = document.getElementById("order-config-form");
                      if (form) {
                        (form as HTMLFormElement).requestSubmit();
                      }
                    }}
                  >
                    Continue to Shipment Details
                  </Button>
                </>
              )}
              {currentStep === 1 && (
                <>
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      // Trigger form submit in ShipmentDetailsStep
                      const form = document.getElementById(
                        "shipment-details-form"
                      );
                      if (form) {
                        (form as HTMLFormElement).requestSubmit();
                      }
                    }}
                  >
                    Continue to Pricing
                  </Button>
                </>
              )}
              {currentStep === 2 && (
                <>
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      // Trigger continue in PricingSelectionStep
                      const continueButton = document.querySelector(
                        "[data-pricing-continue]"
                      );
                      if (continueButton instanceof HTMLElement) {
                        continueButton.click();
                      }
                    }}
                  >
                    Continue to Payment
                  </Button>
                </>
              )}
              {currentStep === 3 && (
                <>
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    type="button"
                    disabled={isCreating}
                    onClick={() => {
                      // Trigger continue in PaymentSelectionStep
                      const continueButton = document.querySelector(
                        "[data-payment-continue]"
                      );
                      if (continueButton instanceof HTMLElement) {
                        continueButton.click();
                      }
                    }}
                  >
                    {isCreating ? "Creating Order..." : "Create Order"}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isCreating && (
          <div className="flex items-center justify-center gap-2 py-4 border-t">
            <Clock className="h-4 w-4 animate-spin" />
            <span className="text-sm text-gray-600">Creating order...</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
